import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import {
  Zap,
  AlertTriangle,
  Package,
  Trash2,
  Loader2,
  CheckCircle,
  XCircle,
  Shield,
  Phone,
  MessageCircle,
  Mail,
  Clock,
  MapPin
} from "lucide-react";

const DestructionExpress = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [reason, setReason] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdDestruction, setCreatedDestruction] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "active" }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const getTotalValue = () => {
    return products
      .filter(p => selectedProducts.includes(p.id))
      .reduce((sum, p) => sum + (p.quantity * p.unit_price), 0);
  };

  const handleSubmit = async () => {
    if (confirmText !== "DETRUIRE") {
      toast.error("Veuillez taper DETRUIRE pour confirmer");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/destructions/express`, {
        product_ids: selectedProducts,
        reason: reason || "Destruction Express - Produits périmés ou invendables",
        priority: "express"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreatedDestruction(response.data);
      setSuccess(true);
      setShowConfirm(false);
      toast.success("Demande de destruction express créée!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CI', { 
      style: 'currency', 
      currency: 'XOF',
      maximumFractionDigits: 0 
    }).format(value);
  };

  if (success && createdDestruction) {
    return (
      <Layout>
        <div className="p-6 md:p-8 flex items-center justify-center min-h-[80vh]">
          <Card className="glass-card max-w-lg w-full text-center border-emerald-200">
            <CardContent className="p-8">
              <div className="w-20 h-20 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-outfit font-bold text-white mb-2">
                Demande Enregistrée!
              </h2>
              <p className="text-slate-500 mb-6">
                Votre demande de destruction express a été créée avec succès.
              </p>
              
              <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-500">Référence:</span>
                  <span className="text-cyan-400 font-mono font-bold">
                    #{createdDestruction.id.slice(0, 8).toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-500">Produits:</span>
                  <span className="text-white font-medium">{createdDestruction.products?.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Valeur totale:</span>
                  <span className="text-amber-400 font-bold">
                    {formatCurrency(createdDestruction.total_value)}
                  </span>
                </div>
              </div>

              {/* Contact Urgent Section */}
              <div className="bg-amber-500/10 border-2 border-amber-300 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-amber-400" />
                  <span className="font-bold text-amber-400">Intervention sous 24h</span>
                </div>
                <p className="text-amber-400 text-sm mb-4">
                  Notre équipe vous contactera rapidement. Pour une intervention immédiate:
                </p>
                <div className="grid grid-cols-1 gap-2">
                  <a 
                    href="tel:+2250768127000" 
                    className="flex items-center justify-center gap-2 bg-cyan-600 text-white py-3 px-4 rounded-lg hover:bg-cyan-700 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    <span className="font-semibold">Appeler CMES: +225 07 68 12 70 00</span>
                  </a>
                  <a 
                    href="https://wa.me/2250768127000?text=Destruction%20Express%20-%20Urgent" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-semibold">WhatsApp Urgence</span>
                  </a>
                </div>
              </div>

              <Button 
                onClick={() => {
                  setSuccess(false);
                  setSelectedProducts([]);
                  setReason("");
                  setConfirmText("");
                  fetchProducts();
                }}
                className="btn-primary w-full"
              >
                Nouvelle demande
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" data-testid="destruction-express-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-red-100 rounded-xl">
                <Zap className="w-7 h-7 text-red-400" />
              </div>
              <div>
                <h1 className="text-3xl font-outfit font-bold text-white">
                  Destruction Express
                </h1>
                <p className="text-slate-500">
                  Service prioritaire - Intervention rapide garantie
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Urgent Contact Banner */}
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Phone className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-bold text-amber-800">Besoin d'une intervention URGENTE?</h3>
                  <p className="text-amber-400 text-sm">
                    Contactez-nous directement pour une prise en charge immédiate
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <a 
                  href="tel:+2250768127000"
                  className="inline-flex items-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 transition-colors font-medium"
                >
                  <Phone className="w-4 h-4" />
                  +225 07 68 12 70 00
                </a>
                <a 
                  href="https://wa.me/2250768127000?text=Destruction%20Express%20Urgente"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
                <a 
                  href="mailto:urgence@cmes.ci"
                  className="inline-flex items-center gap-2 bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 transition-colors font-medium"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Warning Banner */}
        <Card className="glass-card border-red-500/20 bg-red-500/10/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-400 font-medium">Action irréversible</p>
                <p className="text-red-400 text-sm mt-1">
                  Les produits sélectionnés seront marqués pour destruction immédiate. 
                  Cette action ne peut pas être annulée une fois validée.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products Selection */}
        <Card className="glass-card">
          <CardHeader className="flex flex-row items-center justify-between border-b border-white/5 pb-4">
            <CardTitle className="text-white font-outfit flex items-center gap-2">
              <Package className="w-5 h-5 text-cyan-400" />
              Sélection des Produits à Détruire
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={selectAll}
              className="text-cyan-400 hover:text-cyan-400 hover:bg-cyan-500/100/10"
            >
              {selectedProducts.length === products.length ? "Désélectionner tout" : "Tout sélectionner"}
            </Button>
          </CardHeader>
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : products.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {products.map((product) => {
                  const isSelected = selectedProducts.includes(product.id);
                  return (
                    <div
                      key={product.id}
                      onClick={() => toggleProduct(product.id)}
                      data-testid={`product-select-${product.id}`}
                      className={`
                        flex items-center justify-between p-4 rounded-lg cursor-pointer
                        transition-all duration-200 border-2
                        ${isSelected 
                          ? 'bg-red-500/10 border-red-300' 
                          : 'bg-white border-white/10 hover:border-cyan-300'
                        }
                      `}
                    >
                      <div className="flex items-center gap-4">
                        <Checkbox 
                          checked={isSelected}
                          className="data-[state=checked]:bg-red-500/100 data-[state=checked]:border-red-500"
                        />
                        <div>
                          <p className={`font-medium ${isSelected ? 'text-red-400' : 'text-white'}`}>
                            {product.name}
                          </p>
                          <p className="text-slate-500 text-sm">
                            Lot: {product.batch_number} • Exp: {product.expiry_date}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium">x{product.quantity}</p>
                        <p className="text-amber-400 text-sm font-semibold">
                          {formatCurrency(product.quantity * product.unit_price)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucun produit actif disponible</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reason & Submit */}
        {selectedProducts.length > 0 && (
          <Card className="glass-card border-red-500/20">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="text-slate-200 font-medium">Motif de destruction (optionnel)</Label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Produits périmés, invendables, rappel fournisseur..."
                    data-testid="destruction-reason-input"
                    className="bg-white border-white/10 text-white h-24 resize-none focus:border-cyan-400"
                  />
                </div>
                
                <div className="space-y-3">
                  <Label className="text-slate-200 font-medium">Récapitulatif</Label>
                  <div className="bg-white/5 rounded-lg p-4 space-y-3 border border-white/10">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Produits sélectionnés:</span>
                      <span className="text-white font-bold">{selectedProducts.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Valeur totale:</span>
                      <span className="text-amber-400 font-bold text-xl">
                        {formatCurrency(getTotalValue())}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Priorité:</span>
                      <Badge className="bg-red-100 text-red-400 border-red-300">
                        EXPRESS
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col md:flex-row gap-4 justify-end">
                <Button
                  onClick={() => setShowConfirm(true)}
                  data-testid="launch-destruction-btn"
                  className="btn-destruction text-lg px-8 py-6 gap-3"
                >
                  <Zap className="w-6 h-6" />
                  LANCER LA DESTRUCTION EXPRESS
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
          <AlertDialogContent className="bg-white border-red-500/20">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white font-outfit flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                Confirmer la Destruction Express
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-600">
                Vous êtes sur le point de demander la destruction de{" "}
                <span className="text-white font-bold">{selectedProducts.length} produit(s)</span>{" "}
                pour une valeur totale de{" "}
                <span className="text-amber-400 font-bold">{formatCurrency(getTotalValue())}</span>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="py-4">
              <Label className="text-slate-200 mb-2 block">
                Tapez <span className="text-red-400 font-mono font-bold">DETRUIRE</span> pour confirmer
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="DETRUIRE"
                data-testid="confirm-destruction-input"
                className="bg-white border-red-300 text-white font-mono text-center text-lg tracking-widest focus:border-red-500"
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel className="bg-slate-100 border-white/10 text-slate-200 hover:bg-slate-200">
                Annuler
              </AlertDialogCancel>
              <Button
                onClick={handleSubmit}
                disabled={confirmText !== "DETRUIRE" || submitting}
                data-testid="final-confirm-btn"
                className="bg-red-600 hover:bg-red-700 text-white gap-2"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Confirmer la destruction
                  </>
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default DestructionExpress;
